import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, Subject, merge } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, tap, switchMap } from 'rxjs/operators';

import { PlatformService } from '@dx/platform-components';

import { GoToModel } from './go-to.model';
import { GoToEntityType } from './go-to-entity.type';

export { GoToModel, GoToEntityType };

@Injectable({
  providedIn: 'root'
})
export class GoToService {
  searchValue: any;
  searching = false;
  searchingError = false;
  noResults = false;
  termMininum = 3;
  private focus$ = new Subject<string>();

  constructor(private http: HttpClient, private router: Router, private platformService: PlatformService) { }

  navigateToSearch() {
    this.router.navigate(['/search']);
  }

  // used to trigger search on focus
  onFocus(value: string) {
    this.focus$.next(value);
  }

  onSelect(item: any) {
    if (item.otherSearch) {
      this.navigateToSearch();
      return;
    }

    let routePath = '';
    switch (item.entityTypeId) {
      case GoToEntityType.Account:
        routePath = '/account';
        break;
      case GoToEntityType.Billing:
        routePath = '/billing';
        break;
      case GoToEntityType.Claim:
        routePath = '/claim';
        break;
      case GoToEntityType.Policy:
        routePath = '/policy';
        break;
      case GoToEntityType.Agency:
        routePath = '/agency';
        break;
    }

    this.router.navigate([`${routePath}/${item.navigationId}`]);
  }

  reset() {
    this.searchValue = '';
    this.searching = false;
    this.searchingError = false;
    this.noResults = false;
  }

  searchByTerm(term: string) {
    if (term.length < this.termMininum) {
      return of([]);
    }
    const queryParams = new HttpParams().set('query', term);
    return this.http.get<GoToModel[]>(`${this.platformService.apiUrl}/api/search`, { params: queryParams });
  }

  // input value following a selection
  selectionFormatter = (selection: GoToModel) => selection.navigationId;

  typeahead = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(
      debounceTime(300),
      distinctUntilChanged()
    );

    return merge(debouncedText$, this.focus$).pipe(
      tap(() => this.searching = true),
      tap(() => this.searchingError = false),
      tap(() => this.noResults = false),
      switchMap(term =>
        this.searchByTerm(term).pipe(
          tap(results => {
            if (results.length > 0) {
              results.push({ otherSearch: true });
            } else if (term.length >= this.termMininum) {
              this.noResults = true;
            }
          }),
          catchError((resp) => {
            if (resp.error.status === 404) {
              this.noResults = true;
            } else {
              this.searchingError = true;
            }
            return of([]);
          }))
      ),
      tap(() => this.searching = false)
    );
  };
}
